import { drawingCatalog, drawingCatalogById } from "./catalog";
import { DRAWING_ENGINES } from "@/lib/chart-helpers/drawings-KLine/engines";
import type {
  DrawingCatalogEntry,
  DrawingCategoryId,
  DrawingCategoryView,
  DrawingDefinition,
  DrawingInstance,
  DrawingInstanceSettings,
  DrawingStyleSchema,
  DrawingType,
  LineStyle,
} from "./types";
import {
  DATA_FONT,
  DEFAULT_DRAWING_COLOR,
  DEFAULT_FIB_COLOR,
  DEFAULT_FIB_PRESENTATION,
  FIBONACCI_DEFAULT_LEVELS,
  UI_FONT,
} from "./defaults";

/** Implemented drawing types exposed in Add UI and DSL */
export const ENABLED_DRAWING_IDS = new Set<DrawingType>([
  "vertical-line",
  "horizontal-line",
  "trendline",
  "trend-by-angle",
  "equidistant-channel",
  "stddev-channel",
  "regression-channel",
  "andrews-pitchfork",
  "fibonacci-retracement",
  "fibonacci-time-zones",
  "fibonacci-fan",
  "fibonacci-arcs",
  "fibonacci-channel",
  "fibonacci-expansion",
  "gann-line",
  "gann-fan",
  "gann-grid",
  "rectangle",
  "ellipse",
  "triangle",
  "arrowed-line",
  "signal-arrow-up",
  "signal-arrow-down",
  "price-label",
  "cycle-lines",
  "text",
  "text-label",
  "elliott-motive-wave",
  "elliott-corrective-wave",
]);

const STYLE_PRESETS: Record<string, DrawingStyleSchema[]> = {
  "vertical-line": [
    { id: "line", label: "Line", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "horizontal-line": [
    { id: "line", label: "Line", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  trendline: [
    { id: "line", label: "Line", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "trend-by-angle": [
    { id: "line", label: "Line", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "equidistant-channel": [
    { id: "channel", label: "Channel", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "stddev-channel": [
    { id: "channel", label: "Channel", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "regression-channel": [
    { id: "channel", label: "Channel", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "andrews-pitchfork": [
    { id: "channel", label: "Pitchfork", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-retracement": [
    { id: "lines", label: "Lines", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
    { id: "text", label: "Labels", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-time-zones": [
    { id: "lines", label: "Lines", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
    { id: "text", label: "Labels", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-fan": [
    { id: "lines", label: "Lines", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-arcs": [
    { id: "lines", label: "Arcs", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-channel": [
    { id: "lines", label: "Lines", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "fibonacci-expansion": [
    { id: "lines", label: "Lines", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
    { id: "text", label: "Labels", defaultColor: DEFAULT_FIB_COLOR, defaultStyle: "solid" },
  ],
  "gann-line": [
    { id: "line", label: "Line", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "gann-fan": [
    { id: "grid", label: "Fan", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "gann-grid": [
    { id: "grid", label: "Grid", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  rectangle: [
    { id: "fill", label: "Fill", defaultColor: "rgba(33, 150, 243, 0.25)", defaultStyle: "solid" },
    { id: "border", label: "Border", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  ellipse: [
    { id: "fill", label: "Fill", defaultColor: "rgba(33, 150, 243, 0.25)", defaultStyle: "solid" },
    { id: "border", label: "Border", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  triangle: [
    { id: "fill", label: "Fill", defaultColor: "rgba(33, 150, 243, 0.25)", defaultStyle: "solid" },
    { id: "border", label: "Border", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "arrowed-line": [
    { id: "line", label: "Arrow", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "signal-arrow-up": [
    { id: "line", label: "Arrow", defaultColor: "#26a69a", defaultStyle: "solid" },
  ],
  "signal-arrow-down": [
    { id: "line", label: "Arrow", defaultColor: "#ef5350", defaultStyle: "solid" },
  ],
  "cycle-lines": [
    { id: "line", label: "Lines", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "price-label": [
    { id: "background", label: "Background", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
    { id: "text", label: "Text", defaultColor: "#FFFFFF", defaultStyle: "solid" },
  ],
  text: [
    { id: "text", label: "Text", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "text-label": [
    { id: "text", label: "Text", defaultColor: "#FFFFFF", defaultStyle: "solid" },
    { id: "background", label: "Background", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "elliott-motive-wave": [
    { id: "wave", label: "Wave", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
  "elliott-corrective-wave": [
    { id: "wave", label: "Wave", defaultColor: DEFAULT_DRAWING_COLOR, defaultStyle: "solid" },
  ],
};

function buildDefinition(entry: DrawingCatalogEntry): DrawingDefinition {
  const engine = DRAWING_ENGINES[entry.drawingId];
  return {
    ...entry,
    enabled: ENABLED_DRAWING_IDS.has(entry.drawingId),
    styles: STYLE_PRESETS[entry.drawingId] ?? [],
    overlayName: engine?.overlayName ?? entry.drawingId,
  };
}

export const drawingRegistry: Record<string, DrawingDefinition> = Object.fromEntries(
  drawingCatalog.map((entry) => [entry.drawingId, buildDefinition(entry)])
);

export function getDrawingDefinition(
  drawingId: string
): DrawingDefinition | undefined {
  return drawingRegistry[drawingId];
}

export function getDefaultSettings(drawingId: string): DrawingInstanceSettings {
  const def = getDrawingDefinition(drawingId);
  if (!def) return { style: {} };

  const style: Record<string, unknown> = {};
  def.styles.forEach((s) => {
    if (s.id === "line" || s.id === "border" || s.id === "channel" || s.id === "wave" || s.id === "grid") {
      style.color = s.defaultColor;
      style.lineWidth = 1;
      style.lineStyle = s.defaultStyle;
    } else if (s.id === "fill") {
      style.fillColor = s.defaultColor;
    } else if (s.id === "text") {
      style.textColor = s.defaultColor;
      style.fontSize = 11;
      style.fontFamily = UI_FONT;
      style.fontWeight = "normal";
    } else if (s.id === "background") {
      style.backgroundColor = s.defaultColor;
    } else if (s.id === "lines") {
      style.lineColor = s.defaultColor;
      style.textColor = s.defaultColor;
      style.lineWidth = 1;
      style.lineStyle = s.defaultStyle;
      style.fontSize = 10;
      style.fontFamily = DATA_FONT;
      style.fontWeight = "normal";
      Object.assign(style, DEFAULT_FIB_PRESENTATION);
    }
  });

  const values: Record<string, number | string | boolean> = {};
  def.parameters.forEach((p) => {
    values[p.name] = p.defaultValue;
  });

  // Retracement now uses the same custom overlay as its siblings, so it takes
  // the shared level list too.
  const fibCustom = def.drawingId.startsWith("fibonacci-");

  return {
    style,
    values: Object.keys(values).length ? values : undefined,
    fill: values.Fill === true,
    ...(fibCustom ? { levels: FIBONACCI_DEFAULT_LEVELS } : {}),
    ...(def.drawingId === "trend-by-angle"
      ? { angle: (values.Angle as number) ?? 45 }
      : {}),
  };
}

export function catalogEntryToInstance(
  drawingId: string,
  partial?: Partial<DrawingInstance>
): DrawingInstance {
  const catalog = drawingCatalogById[drawingId as DrawingType];
  const def = getDrawingDefinition(drawingId);
  if (!catalog || !def) {
    throw new Error(`Unknown drawing: ${drawingId}`);
  }
  return {
    id:
      partial?.id ??
      `draw_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    name: partial?.name ?? catalog.name,
    symbolKey: partial?.symbolKey ?? "",
    windowId: partial?.windowId ?? "",
    chartPanelId: partial?.chartPanelId,
    type: catalog.drawingId,
    visible: partial?.visible ?? true,
    settings: partial?.settings ?? getDefaultSettings(drawingId),
    addedAt: partial?.addedAt ?? Date.now(),
    ...partial,
  };
}

export function getDrawingName(type: DrawingType): string {
  return drawingCatalogById[type]?.name ?? type;
}

export function requiresTextPrompt(drawingId: string): boolean {
  return Boolean(
    getDrawingDefinition(drawingId)?.requiresTextPrompt ??
      drawingCatalogById[drawingId as DrawingType]?.requiresTextPrompt
  );
}

export interface DrawingCategoryViewEntry extends DrawingCatalogEntry {
  enabled: boolean;
}

export function getEnabledDrawingCategories(): DrawingCategoryView[] {
  const byCategory = new Map<DrawingCategoryId, DrawingCategoryView>();

  for (const entry of drawingCatalog) {
    if (!ENABLED_DRAWING_IDS.has(entry.drawingId)) continue;
    const def = getDrawingDefinition(entry.drawingId);
    if (!def?.enabled) continue;

    let cat = byCategory.get(entry.category);
    if (!cat) {
      cat = {
        id: entry.category,
        name: getCategoryDisplayName(entry.category),
        drawings: [],
      };
      byCategory.set(entry.category, cat);
    }
    cat.drawings.push(entry);
  }

  const order: DrawingCategoryId[] = [
    "lines",
    "channels",
    "fibonacci",
    "gann",
    "shapes",
    "symbols-arrows",
    "text",
    "elliott",
  ];

  return order
    .map((id) => byCategory.get(id))
    .filter((c): c is DrawingCategoryView => c != null && c.drawings.length > 0);
}

function getCategoryDisplayName(id: DrawingCategoryId): string {
  const labels: Record<DrawingCategoryId, string> = {
    lines: "Lines",
    channels: "Channels",
    fibonacci: "Fibonacci",
    gann: "Gann",
    shapes: "Shapes",
    "symbols-arrows": "Symbols & Arrows",
    text: "Text",
    elliott: "Elliott",
  };
  return labels[id];
}

export type { LineStyle, DrawingStyleSchema };
